import React, { Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Link, Navigate } from "react-router-dom";
import AuthProvider from "./auth/AuthProvider";
import RequireAuth from "./auth/RequireAuth";
import Layout from "./components/Layout";
import { Toaster } from "./components/ui/sonner";
import Dashboard from "./pages/Dashboard";
import Login from "./pages/auth/Login";
import AuthShell from "./components/AuthShell";

// Phase J — every route past the cold-start landing is lazy-loaded.
// Eager imports are Layout + Dashboard + Login (most common entry paths);
// everything else lands in its own chunk and only ships when navigated to.
const Documents = lazy(() => import("./pages/Documents"));
const UploadPage = lazy(() => import("./pages/Upload"));
const CreateDocument = lazy(() => import("./pages/CreateDocument"));
const Contractors = lazy(() => import("./pages/Contractors"));
const Correspondence = lazy(() => import("./pages/Correspondence"));
const CompanyDocuments = lazy(() => import("./pages/CompanyDocuments"));
const Templates = lazy(() => import("./pages/Templates"));
const TemplateEditor = lazy(() => import("./pages/TemplateEditor"));
const Team = lazy(() => import("./pages/Team"));
const SettingsOrganization = lazy(() =>
  import("./pages/SettingsOrganization"),
);
const SettingsAuditLog = lazy(() => import("./pages/SettingsAuditLog"));
const SettingsDirectory = lazy(() => import("./pages/SettingsDirectory"));
const SettingsOrgStructure = lazy(() => import("./pages/SettingsOrgStructure"));
const SettingsDocumentTypes = lazy(() => import("./pages/SettingsDocumentTypes"));
const SettingsLayout = lazy(() => import("./pages/SettingsLayout"));
const Integrations = lazy(() => import("./pages/Integrations"));
const IntegrationsApi = lazy(() => import("./pages/IntegrationsApi"));
const Integrations1C = lazy(() => import("./pages/Integrations1C"));
const IntegrationsWebhooks = lazy(() => import("./pages/IntegrationsWebhooks"));
const TemplateComposer = lazy(() => import("./pages/TemplateComposer"));
const SettingsNotifications = lazy(() =>
  import("./pages/SettingsNotifications"),
);
const Notifications = lazy(() => import("./pages/Notifications"));
const Forms = lazy(() => import("./pages/Forms"));
const Workflows = lazy(() => import("./pages/Workflows"));
const WorkflowOverview = lazy(() => import("./pages/WorkflowOverview"));
const Inbox = lazy(() => import("./pages/Inbox"));
const FormPreview = lazy(() => import("./pages/FormPreview"));
const FormSubmissions = lazy(() => import("./pages/FormSubmissions"));
const Signup = lazy(() => import("./pages/auth/Signup"));
const EcpLogin = lazy(() => import("./pages/auth/EcpLogin"));
const VerifyEmail = lazy(() => import("./pages/auth/VerifyEmail"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
const AcceptInvite = lazy(() => import("./pages/auth/AcceptInvite"));

const DocumentView = lazy(() => import("./pages/DocumentView"));
const SignDocument = lazy(() => import("./pages/SignDocument"));
const PublicSign = lazy(() => import("./pages/PublicSign"));
const WorkflowEditor = lazy(() => import("./pages/WorkflowEditor"));
const WorkflowInstanceView = lazy(() => import("./pages/WorkflowInstanceView"));
const FormEditor = lazy(() => import("./pages/FormEditor"));
const Reports = lazy(() => import("./pages/Reports"));
const NotFound = lazy(() => import("./pages/NotFound"));

function RouteFallback() {
  return (
    <div
      className="p-8 text-sm text-zinc-500"
      role="status"
      aria-live="polite"
      data-testid="route-fallback"
    >
      …
    </div>
  );
}

function NoOrgs() {
  return (
    <AuthShell
      title="Нет доступных организаций"
      subtitle="Ваш аккаунт пока не состоит ни в одной активной организации. Создайте новую или примите приглашение."
      footer={
        <Link to="/login" className="text-primary hover:underline">
          Выйти и сменить аккаунт
        </Link>
      }
    >
      <div className="text-sm text-zinc-600">
        Если вас уже приглашали — попросите администратора прислать ссылку
        повторно.
      </div>
    </AuthShell>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Public / auth shell routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/ecp" element={<EcpLogin />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/no-orgs" element={<NoOrgs />} />

            {/* Public signing portal — unauthenticated, token-gated */}
            <Route path="/sign/:token" element={<PublicSign />} />

            {/* Authenticated app */}
            <Route element={<RequireAuth />}>
              {/* Focused signing surface — auth-gated but OUTSIDE the app
                  Layout, so an internal signer gets the same clean, sidebar-free
                  "review & sign" view an external signer sees at /sign/:token. */}
              <Route path="/documents/:id/sign" element={<SignDocument />} />
              <Route element={<Layout />}>
                <Route path="/" element={<Dashboard />} />
                <Route path="/inbox" element={<Inbox />} />
                <Route path="/documents" element={<Documents />} />
                <Route path="/documents/:id" element={<DocumentView />} />
                <Route path="/upload" element={<UploadPage />} />
                <Route path="/documents/new" element={<CreateDocument />} />
                <Route path="/contractors" element={<Contractors />} />
                <Route path="/correspondence" element={<Correspondence />} />
                <Route path="/company-documents" element={<CompanyDocuments />} />
                <Route path="/templates" element={<Templates />} />
                <Route path="/templates/compose" element={<TemplateComposer />} />
                <Route path="/templates/:id/edit" element={<TemplateEditor />} />
                <Route path="/team" element={<Team />} />
                <Route path="/forms" element={<Forms />} />
                <Route path="/forms/:id/edit" element={<FormEditor />} />
                <Route path="/forms/:id/preview" element={<FormPreview />} />
                <Route
                  path="/forms/:id/submissions"
                  element={<FormSubmissions />}
                />
                <Route path="/workflows" element={<Workflows />} />
                <Route
                  path="/workflows/:id"
                  element={<WorkflowOverview />}
                />
                <Route
                  path="/workflows/:id/edit"
                  element={<WorkflowEditor />}
                />
                <Route
                  path="/workflow-instances/:id"
                  element={<WorkflowInstanceView />}
                />
                {/* Phase R: настройки — один раздел с табами; старые
                    URL-ы сохраняются как вложенные роуты */}
                <Route path="/integrations" element={<Integrations />} />
                <Route path="/integrations/api" element={<IntegrationsApi />} />
                <Route path="/integrations/1c" element={<Integrations1C />} />
                <Route
                  path="/integrations/webhooks"
                  element={<IntegrationsWebhooks />}
                />
                <Route path="/settings" element={<SettingsLayout />}>
                  <Route
                    index
                    element={<Navigate to="/settings/organization" replace />}
                  />
                  <Route
                    path="organization"
                    element={<SettingsOrganization />}
                  />
                  <Route path="audit" element={<SettingsAuditLog />} />
                  <Route
                    path="notifications"
                    element={<SettingsNotifications />}
                  />
                  <Route
                    path="integrations"
                    element={<Navigate to="/integrations" replace />}
                  />
                  <Route path="directory" element={<SettingsDirectory />} />
                  <Route
                    path="org-structure"
                    element={<SettingsOrgStructure />}
                  />
                  <Route
                    path="document-types"
                    element={<SettingsDocumentTypes />}
                  />
                </Route>
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/reports" element={<Reports />} />
              </Route>
            </Route>

            {/* UX-16: catch-all. Без него неизвестный URL рендерил пустую
                белую страницу без единого способа вернуться. */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          {/* Требование: ошибки в правом нижнем углу ПО ВСЕЙ СИСТЕМЕ.
              Раньше Toaster жил внутри Layout, поэтому страницы вне него —
              вход, регистрация, /sign/:token и /documents/:id/sign — вообще
              не имели тостера: их сообщения (включая «Документ подписан» из
              EcpSignModal) не показывались никогда. Здесь он один на всё
              приложение и покрывает каждый маршрут. */}
          <Toaster
            richColors
            theme="light"
            position="bottom-right"
            closeButton
            duration={6000}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
